import React, {useState, useEffect, useMemo, useRef} from 'react'
import {KTSVG} from '../../_metronic/helpers'
import useAuth from '../../hooks/useAuth'
import {useConnectedUsersCount} from '../../hooks/useConnectedUsersCount'
import {useAdminStatus} from '../../hooks/useAdminStatus'
import {useSupportChat} from '../../hooks/useSupportChat'
import useUsers from '../../hooks/useUsers'
import {useActiveConversation} from '../../contexts/ActiveConversationContext'
import useNotifications from '../../hooks/useNotifications'
import type {User} from '../../models'
import type {ChatMessage, ChatConversation, MessageType} from '../../services/chatSupportTypes'
import {getConversationId} from '../../services/chatSupportTypes'
import './FloatingConnectedUsers.css'

/** Set to true to show chat messaging (floating button + panel). Hidden until feature is ready. */
export const CHAT_MESSAGING_ENABLED = true

/** Display name for admin in "who is connected" (non-admin view) */
const ADMIN_DISPLAY_NAME = 'El Farabi'

/** Quick-insert emojis for chat (users and admin can send emojis) */
const CHAT_QUICK_EMOJIS = ['😊', '👍', '❤️', '😂', '🙏', '😅', '👋', '✅', '🎉', '💪', '😢', '🔥']

/** Max message length (backend). Screenshot data URLs must stay under this. */
const CHAT_CONTENT_MAX_LENGTH = 50000

/**
 * Resize/compress image to a data URL under CHAT_CONTENT_MAX_LENGTH (for sending as message content).
 * Returns Promise<string> data URL or rejects if conversion fails.
 */
function imageToDataUrlForChat(fileOrBlob: File | Blob, maxLength = CHAT_CONTENT_MAX_LENGTH - 500): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img')
    const url = URL.createObjectURL(fileOrBlob)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const maxWidth = 640
      const maxHeight = 480
      let { width, height } = img
      if (width > maxWidth || height > maxHeight) {
        const r = Math.min(maxWidth / width, maxHeight / height)
        width = Math.round(width * r)
        height = Math.round(height * r)
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Canvas not supported'))
        return
      }
      ctx.drawImage(img, 0, 0, width, height)
      let quality = 0.7
      const tryEncode = () => {
        const dataUrl = canvas.toDataURL('image/jpeg', quality)
        if (dataUrl.length <= maxLength) {
          resolve(dataUrl)
          return
        }
        quality -= 0.15
        if (quality < 0.2) {
          resolve(dataUrl)
          return
        }
        tryEncode()
      }
      tryEncode()
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }
    img.src = url
  })
}

/** True if message content is an inline image (data URL). */
function isImageContent(content: string): boolean {
  return typeof content === 'string' && content.startsWith('data:image/')
}

function getClasseLabel(user: User | undefined): string {
  if (!user) return '—'
  const classroom = user.classrooms?.[0] ?? user.previousClasses?.[0]
  return classroom?.name ?? '—'
}

function getName(user: User | undefined, id: string): string {
  if (!user) return id
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || id
}

function formatMessageTime(createdAt: string): string {
  try {
    const d = new Date(createdAt)
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    return sameDay
      ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
          ' ' +
          d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

/** Chat with admin is available to all non-admin roles (3–10), not only students. */
export default function FloatingConnectedUsers() {
  const {me} = useAuth()
  const isAdmin = String(me?.role) === '2'
  const currentUserId = me?._id
  const {onlineIds, count, loading, error} = useConnectedUsersCount(!!isAdmin)
  const {online: adminOnline, loading: adminLoading, error: adminError} = useAdminStatus(
    !!me && !isAdmin
  )
  const {
    conversations,
    selectedConversation,
    setSelectedConversation,
    messages,
    loading: chatLoading,
    sending,
    error: chatError,
    chatAvailable,
    loadConversationWithAdmin,
    joinExistingConversationWithAdmin,
    loadConversations,
    refreshConversations,
    sendMessage,
    totalUnread,
    conversationWithAdmin,
    startNewConversationWithAdmin,
    startChatWithUser,
    deleteConversation,
    isAiTyping,
    loadAiConversation,
    switchToAdminConversation,
    isAiConversation,
  } = useSupportChat(isAdmin)
  const {users, fetchAllUsers, listLoading} = useUsers()
  const {setActiveConversationId} = useActiveConversation()
  const {notifications, handleUnseenNotification} = useNotifications()
  const [isOpen, setIsOpen] = useState(false)
  const [usersFetched, setUsersFetched] = useState(false)
  /** User: 'status' | 'chat' | 'select-conversation'. Admin: 'list' | 'chats' | 'chat' */
  const [view, setView] = useState<'status' | 'chat' | 'list' | 'chats' | 'select-conversation'>('status')
  const [chatInput, setChatInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pendingConversationIdRef = useRef<string | null>(null)

  // Prefetch users for admin as soon as component mounts (or when hovering button) to avoid showing IDs
  useEffect(() => {
    if (isAdmin && !usersFetched && users.length === 0) {
      fetchAllUsers({})
      setUsersFetched(true)
    }
  }, [isAdmin, usersFetched, users.length, fetchAllUsers])

  const usersById = useMemo(() => {
    const map: Record<string, User> = {}
    users.forEach((u) => {
      if (u._id) map[u._id] = u
    })
    return map
  }, [users])

  const list = useMemo(() => {
    return onlineIds.map((id) => ({
      id,
      name: getName(usersById[id], id),
      classe: getClasseLabel(usersById[id]),
    }))
  }, [onlineIds, usersById])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }
  useEffect(() => {
    if (view === 'chat' && messages.length) scrollToBottom()
  }, [view, messages.length])

  const handleOpenChatWithAdmin = () => {
    // Always show conversation selection view
    setView('select-conversation')
    // Load conversations if not already loaded
    if (conversations.length === 0) {
      loadConversations()
    }
  }

  const handleSelectConversationToJoin = (conv: ChatConversation) => {
    setSelectedConversation(conv)
    setView('chat')
    // Socket join happens automatically via useEffect when selectedConversation changes
    // Messages will be loaded if not already loaded
  }

  const handleCreateNewConversation = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (window.confirm('Start a new conversation? Your current chat history will be cleared.')) {
      setView('chat')
      startNewConversationWithAdmin()
    }
  }

  const handleAdminOpenMessages = () => {
    setView('chats')
    loadConversations()
  }

  const handleSelectConversation = (conv: typeof selectedConversation) => {
    setSelectedConversation(conv)
    setView('chat')
  }

  const handleSend = () => {
    const text = chatInput.trim()
    if (!text || sending) return
    sendMessage(text)
    setChatInput('')
  }

  const sendImageAsMessage = (dataUrl: string) => {
    if (sending || (!isAdmin && !selectedConversation)) return
    if (dataUrl.length > CHAT_CONTENT_MAX_LENGTH) return
    sendMessage(dataUrl)
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const item = e.clipboardData?.items?.[0]
    if (!item || item.kind !== 'file' || !item.type.startsWith('image/')) return
    e.preventDefault()
    const blob = item.getAsFile()
    if (!blob) return
    imageToDataUrlForChat(blob)
      .then(sendImageAsMessage)
      .catch(() => {})
  }

  const handleAttachImage = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !file.type.startsWith('image/')) return
    imageToDataUrlForChat(file)
      .then(sendImageAsMessage)
      .catch(() => {})
  }

  const handleBackFromChat = () => {
    if (isAdmin) {
      setView('chats')
      setSelectedConversation(null)
    } else {
      setView('status')
    }
  }

  const handleBackFromChats = () => {
    setView('list')
  }

  const handleStartNewConversation = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (window.confirm('Start a new conversation? Your current chat history will be cleared.')) {
      startNewConversationWithAdmin()
    }
  }

  const handleDeleteConversation = () => {
    if (!selectedConversation) return
    const convId = getConversationId(selectedConversation)
    if (!convId) {
      console.error('Cannot delete conversation: missing ID')
      return
    }
    if (window.confirm(`Delete this conversation with ${otherParticipantName(selectedConversation)}? All messages will be permanently removed.`)) {
      deleteConversation(convId)
      setView('chats')
    }
  }

  /** Update active conversation context when selectedConversation changes */
  useEffect(() => {
    const convId = getConversationId(selectedConversation)
    setActiveConversationId(convId)
  }, [selectedConversation, setActiveConversationId])

  /** Mark chat notifications as read when viewing conversation (per FRONTEND-CHAT-MESSAGE-NOTIFICATIONS.md) */
  useEffect(() => {
    const convId = getConversationId(selectedConversation)
    if (!convId || !me?._id) return

    // Find unread chat notifications for this conversation
    const chatNotifications = notifications.filter(
      (n) =>
        n.notificationType === 'NEW_CHAT_MESSAGE' &&
        !n.read &&
        n.url &&
        n.url.includes(`/chat/${convId}`)
    )

    // Mark them as read
    if (chatNotifications.length > 0) {
      chatNotifications.forEach((notification) => {
        handleUnseenNotification({ notificationId: notification._id }).catch(() => {
          // Silently fail if notification already read or doesn't exist
        })
      })
    }
  }, [selectedConversation, notifications, me?._id, handleUnseenNotification])

  /** When panel opens (user), refresh conversations so unread badge is correct. */
  useEffect(() => {
    if (isOpen && !isAdmin) refreshConversations()
  }, [isOpen, isAdmin, refreshConversations])

  const showMessagesLink = isAdmin && chatAvailable !== false

  /** Listen for chat notification clicks to open conversation */
  useEffect(() => {
    const handleChatNotificationClick = (e: CustomEvent<{ conversationId: string }>) => {
      const { conversationId } = e.detail
      if (!conversationId) return

      // Find conversation in current list
      const conv = conversations.find((c) => getConversationId(c) === conversationId)
      if (conv) {
        setIsOpen(true)
        setSelectedConversation(conv)
        setView('chat')
        pendingConversationIdRef.current = null
      } else {
        // Conversation not in list - mark as pending and load conversations
        pendingConversationIdRef.current = conversationId
        setIsOpen(true)
        if (isAdmin) {
          setView('chats')
        } else {
          setView('select-conversation')
        }
        loadConversations()
      }
    }

    window.addEventListener('openChatConversation' as any, handleChatNotificationClick as EventListener)
    return () => {
      window.removeEventListener('openChatConversation' as any, handleChatNotificationClick as EventListener)
    }
  }, [conversations, isAdmin, setSelectedConversation, loadConversations])

  /** Auto-select conversation when it becomes available (for notification clicks) */
  useEffect(() => {
    const pendingId = pendingConversationIdRef.current
    if (!pendingId) return

    const conv = conversations.find((c) => getConversationId(c) === pendingId)
    if (conv) {
      setSelectedConversation(conv)
      setView('chat')
      pendingConversationIdRef.current = null
    }
  }, [conversations, setSelectedConversation])

  /** Reset view when panel closes. */
  useEffect(() => {
    if (!isOpen) {
      setView(isAdmin ? 'list' : 'status')
      setActiveConversationId(null) // Clear active conversation when panel closes
    }
  }, [isOpen, isAdmin, setActiveConversationId])

  const otherParticipantName = (conv: typeof selectedConversation): string => {
    if (!conv || !me) return ''
    const other = conv.participants.find((p) => p._id !== me._id)
    return other ? [other.firstName, other.lastName].filter(Boolean).join(' ') || other._id : 'User'
  }

  if (!me) return null

  const showUnreadBadge = !isAdmin && totalUnread > 0

  return (
    <div className='floating-connected-users-container'>
      <button
        type='button'
        className='floating-connected-users-button'
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsOpen((o) => !o)
        }}
        onMouseEnter={() => {
          // Prefetch users on hover for faster loading when modal opens
          if (isAdmin && !usersFetched && users.length === 0) {
            fetchAllUsers({})
            setUsersFetched(true)
          }
        }}
        title={isAdmin ? 'Who is connected / Messages' : 'Admin & Chat'}
        aria-label={isAdmin ? 'Who is connected' : 'Admin & Chat'}
      >
        <KTSVG path='/media/icons/duotune/communication/com006.svg' className='svg-icon-2' />
        {isAdmin && count !== null && count > 0 && (
          <span className='floating-connected-users-badge'>{count}</span>
        )}
        {!isAdmin && adminOnline !== null && adminOnline && (
          <span className='floating-connected-users-badge floating-connected-users-badge-dot' />
        )}
        {showUnreadBadge && (
          <span className='floating-connected-users-badge floating-connected-users-unread'>
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {isOpen && (
        <div className='floating-connected-users-panel'>
          {/* ---------- Admin: Connected users list ---------- */}
          {isAdmin && view === 'list' && (
            <>
              <div className='floating-connected-users-panel-header'>
                <h3 className='floating-connected-users-panel-title'>Connected users</h3>
                <div className='floating-connected-users-panel-actions'>
                  {showMessagesLink ? (
                    <button
                      type='button'
                      className='floating-connected-users-link'
                      onClick={handleAdminOpenMessages}
                    >
                      Messages
                    </button>
                  ) : (
                    <span className='floating-connected-users-coming-soon'>Messages (coming soon)</span>
                  )}
                  <button
                    type='button'
                    className='floating-connected-users-panel-close'
                    onClick={() => setIsOpen(false)}
                    aria-label='Close'
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                  </button>
                </div>
              </div>
              <div className='floating-connected-users-panel-body'>
                {error && (
                  <p className='floating-connected-users-error text-danger small'>{error}</p>
                )}
                {loading || listLoading ? (
                  <p className='text-muted small'>Loading user names…</p>
                ) : list.length === 0 ? (
                  <p className='text-muted small'>No users connected</p>
                ) : (
                  <ul className='floating-connected-users-list'>
                    {list.map((item) => (
                      <li
                        key={item.id}
                        className='floating-connected-users-item floating-connected-users-item-clickable'
                        onClick={() => {
                          startChatWithUser(item.id)
                          setView('chat')
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className='bullet bullet-dot bg-success me-2' />
                        <span className='floating-connected-users-item-name'>{item.name}</span>
                        <span className='floating-connected-users-item-classe text-muted'>
                          {item.classe}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}

          {/* ---------- Admin: Conversations list ---------- */}
          {isAdmin && view === 'chats' && (
            <>
              <div className='floating-connected-users-panel-header'>
                <button
                  type='button'
                  className='floating-connected-users-back'
                  onClick={handleBackFromChats}
                  aria-label='Back'
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-2' />
                </button>
                <h3 className='floating-connected-users-panel-title'>Messages</h3>
                <button
                  type='button'
                  className='floating-connected-users-panel-close'
                  onClick={() => setIsOpen(false)}
                  aria-label='Close'
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                </button>
              </div>
              <div className='floating-connected-users-panel-body'>
                {chatError && (
                  <p className='floating-connected-users-error text-danger small'>{chatError}</p>
                )}
                {chatLoading && conversations.length === 0 ? (
                  <p className='text-muted small'>Loading…</p>
                ) : conversations.length === 0 ? (
                  <p className='text-muted small'>No conversations yet</p>
                ) : (
                  <ul className='floating-connected-users-list'>
                    {conversations
                      .filter((conv) => {
                        // CRITICAL SECURITY: Double-check user is participant (should already be filtered)
                        if (!isAdmin) {
                          const participantIds = conv.participants.map((p) => p._id)
                          const isParticipant = me?._id && participantIds.includes(me._id)
                          if (!isParticipant) {
                            console.error('🚨 SECURITY: Attempted to display conversation user is not part of!', {
                              conversationId: getConversationId(conv),
                              participants: participantIds,
                              currentUserId: me?._id,
                            })
                            return false
                          }
                          // Verify exactly 2 participants (user + admin)
                          if (conv.participants.length !== 2) {
                            console.error('🚨 SECURITY: Conversation has wrong number of participants!', {
                              conversationId: conv._id,
                              participantCount: conv.participants.length,
                              expected: 2,
                            })
                            return false
                          }
                        }
                        return true
                      })
                      .map((conv) => (
                        <li
                          key={getConversationId(conv) ?? 'unknown'}
                          className='floating-connected-users-item floating-connected-users-item-clickable floating-connected-users-conv-item'
                          onClick={() => handleSelectConversation(conv)}
                        >
                          <div className='floating-connected-users-conv-row'>
                            <span className='floating-connected-users-item-name'>
                              {conv.participants
                                .filter((p) => p._id !== me?._id)
                                .map((p) => [p.firstName, p.lastName].filter(Boolean).join(' '))
                                .join(', ') || 'User'}
                            </span>
                            {(conv.unreadCount ?? 0) > 0 && (
                              <span className='badge badge-circle badge-primary badge-sm'>
                                {conv.unreadCount}
                              </span>
                            )}
                          </div>
                          {conv.lastMessage && (
                            <span className='floating-connected-users-item-preview text-muted'>
                              {isImageContent(conv.lastMessage.content)
                                ? '📷 Image'
                                : (conv.lastMessage.content.slice(0, 40) + (conv.lastMessage.content.length > 40 ? '…' : ''))}
                            </span>
                          )}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </>
          )}

          {/* ---------- User: Select Conversation (create or join) ---------- */}
          {!isAdmin && view === 'select-conversation' && (
            <>
              <div className='floating-connected-users-panel-header'>
                <button
                  type='button'
                  className='floating-connected-users-back'
                  onClick={() => setView('status')}
                  aria-label='Back'
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-2' />
                </button>
                <h3 className='floating-connected-users-panel-title'>Chat</h3>
                <button
                  type='button'
                  className='floating-connected-users-panel-close'
                  onClick={() => setIsOpen(false)}
                  aria-label='Close'
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                </button>
              </div>
              <div className='floating-connected-users-panel-body'>
                {chatError && (
                  <p className='floating-connected-users-error text-danger small'>{chatError}</p>
                )}
                {chatLoading && conversations.length === 0 ? (
                  <p className='text-muted small'>Loading conversations…</p>
                ) : (
                  <>
                    <div className='mb-3'>
                      <button
                        type='button'
                        className='btn btn-primary w-100 mb-2'
                        onClick={async () => {
                          await loadAiConversation()
                          setView('chat')
                        }}
                        disabled={chatLoading}
                      >
                        <i className='bi bi-robot me-2' />
                        Chat with AI
                      </button>
                      <p className='text-muted small mb-3'>
                        Talk to the assistant in any language. Need a human? Use &quot;Talk to admin&quot; in the chat.
                      </p>
                    </div>
                    <div className='mb-3'>
                      <button
                        type='button'
                        className='btn btn-outline-primary w-100'
                        onClick={handleCreateNewConversation}
                        disabled={chatLoading}
                      >
                        <KTSVG path='/media/icons/duotune/general/gen041.svg' className='svg-icon-3 me-2' />
                        Talk to admin
                      </button>
                      <p className='text-muted small mt-2 mb-0'>
                        Start a fresh conversation with a human (clears any existing history)
                      </p>
                    </div>
                    {conversations.length > 0 && (
                      <>
                        <div className='d-flex align-items-center my-3'>
                          <div className='flex-grow-1 border-top'></div>
                          <span className='text-muted small mx-2'>OR</span>
                          <div className='flex-grow-1 border-top'></div>
                        </div>
                        <h4 className='small mb-2'>Join Existing Conversation</h4>
                        <ul className='floating-connected-users-list'>
                          {conversations
                            .filter((conv) => {
                              const participantIds = conv.participants.map((p) => p._id)
                              return me?._id && participantIds.includes(me._id) && conv.participants.length === 2
                            })
                            .map((conv) => (
                              <li
                                key={getConversationId(conv) ?? 'unknown'}
                                className='floating-connected-users-item floating-connected-users-item-clickable floating-connected-users-conv-item'
                                onClick={() => handleSelectConversationToJoin(conv)}
                              >
                                <div className='floating-connected-users-conv-row'>
                                  <span className='floating-connected-users-item-name'>
                                    {conv.participants
                                      .filter((p) => p._id !== me?._id)
                                      .map((p) => [p.firstName, p.lastName].filter(Boolean).join(' '))
                                      .join(', ') || 'Admin'}
                                  </span>
                                  {(conv.unreadCount ?? 0) > 0 && (
                                    <span className='badge badge-circle badge-primary badge-sm'>
                                      {conv.unreadCount}
                                    </span>
                                  )}
                                </div>
                                {conv.lastMessage && (
                                  <span className='floating-connected-users-item-preview text-muted'>
                                    {isImageContent(conv.lastMessage.content)
                                      ? '📷 Image'
                                      : (conv.lastMessage.content.slice(0, 40) + (conv.lastMessage.content.length > 40 ? '…' : ''))}
                                  </span>
                                )}
                                {conv.updatedAt && (
                                  <span className='floating-connected-users-item-time text-muted small'>
                                    {formatMessageTime(conv.updatedAt)}
                                  </span>
                                )}
                              </li>
                            ))}
                        </ul>
                        {conversations.filter((conv) => {
                          const participantIds = conv.participants.map((p) => p._id)
                          return me?._id && participantIds.includes(me._id) && conv.participants.length === 2
                        }).length === 0 && (
                          <p className='text-muted small'>No existing conversations found</p>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* ---------- User: Status (who is connected) ---------- */}
          {!isAdmin && view === 'status' && (
            <>
              <div className='floating-connected-users-panel-header'>
                <h3 className='floating-connected-users-panel-title'>Who is connected</h3>
                <button
                  type='button'
                  className='floating-connected-users-panel-close'
                  onClick={() => setIsOpen(false)}
                  aria-label='Close'
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                </button>
              </div>
              <div className='floating-connected-users-panel-body'>
                {adminError && (
                  <p className='floating-connected-users-error text-danger small'>{adminError}</p>
                )}
                {adminLoading && adminOnline === null ? (
                  <p className='text-muted small'>Loading…</p>
                ) : (
                  <div
                    className={`floating-connected-users-item floating-connected-users-admin-row ${chatAvailable !== false ? 'floating-connected-users-item-clickable' : ''}`}
                    onClick={handleOpenChatWithAdmin}
                    role='button'
                    tabIndex={chatAvailable !== false ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (chatAvailable === false) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleOpenChatWithAdmin()
                      }
                    }}
                  >
                    <span
                      className={`bullet bullet-dot me-2 ${adminOnline ? 'bg-success' : 'bg-secondary'}`}
                    />
                    <span className='floating-connected-users-item-name'>
                      Admin {ADMIN_DISPLAY_NAME}
                    </span>
                    <span className='floating-connected-users-item-classe text-muted'>
                      {adminOnline === null ? '—' : adminOnline ? 'Online' : 'Offline'}
                    </span>
                    {chatAvailable === false ? (
                      <span className='floating-connected-users-chat-hint text-muted'>Chat (coming soon)</span>
                    ) : (
                      <span className='floating-connected-users-chat-hint'>Click to chat</span>
                    )}
                    {chatAvailable !== false && conversationWithAdmin && (conversationWithAdmin.unreadCount ?? 0) > 0 && (
                      <span className='badge badge-circle badge-primary badge-sm ms-1'>
                        {conversationWithAdmin.unreadCount}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ---------- Chat view (user with admin, or admin with selected user) ---------- */}
          {view === 'chat' && (isAdmin ? selectedConversation : true) && (
            <>
              <div className='floating-connected-users-panel-header'>
                <button
                  type='button'
                  className='floating-connected-users-back'
                  onClick={handleBackFromChat}
                  aria-label='Back'
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-2' />
                </button>
                <h3 className='floating-connected-users-panel-title'>
                  {isAdmin
                    ? otherParticipantName(selectedConversation)
                    : isAiConversation
                      ? 'Chat with AI'
                      : `Chat with Admin ${ADMIN_DISPLAY_NAME}`}
                </h3>
                <div className='d-flex align-items-center gap-1'>
                  {!isAdmin && (
                    <>
                      {selectedConversation ? (
                        isAiConversation ? (
                          <button
                            type='button'
                            className='btn btn-sm btn-light-primary'
                            onClick={async () => {
                              await switchToAdminConversation()
                            }}
                            disabled={chatLoading}
                            title='Switch to human support'
                            aria-label='Talk to admin'
                          >
                            Talk to admin
                          </button>
                        ) : (
                          <button
                            type='button'
                            className='btn btn-sm btn-light-danger'
                            onClick={handleStartNewConversation}
                            disabled={chatLoading}
                            title='Start new conversation (clears current history)'
                            aria-label='Start new conversation'
                          >
                            New Chat
                          </button>
                        )
                      ) : (
                        <div className='d-flex gap-1'>
                          <button
                            type='button'
                            className='btn btn-sm btn-light-primary'
                            onClick={() => {
                              joinExistingConversationWithAdmin()
                              setView('chat')
                            }}
                            disabled={chatLoading}
                            title='Join existing conversation'
                            aria-label='Join existing conversation'
                          >
                            Join
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-light-danger'
                            onClick={handleStartNewConversation}
                            disabled={chatLoading}
                            title='Start new conversation (clears history)'
                            aria-label='Start new conversation'
                          >
                            New
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  {isAdmin && selectedConversation && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light-danger'
                      onClick={handleDeleteConversation}
                      title='Delete conversation'
                      aria-label='Delete conversation'
                    >
                      Delete
                    </button>
                  )}
                  <button
                    type='button'
                    className='floating-connected-users-panel-close'
                    onClick={() => setIsOpen(false)}
                    aria-label='Close'
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                  </button>
                </div>
              </div>
              <div className='floating-connected-users-chat-messages'>
                {chatError && (
                  <p className='floating-connected-users-error text-danger small'>{chatError}</p>
                )}
                {chatLoading && messages.length === 0 ? (
                  <p className='text-muted small p-3'>Loading…</p>
                ) : messages.length === 0 ? (
                  <p className='text-muted small p-3'>No messages yet. Say hello!</p>
                ) : (
                  messages.map((msg) => {
                    const senderId = typeof msg.senderId === 'object' && msg.senderId !== null ? msg.senderId._id : String(msg.senderId)
                    const isMe = senderId === me?._id
                    const messageType: MessageType = msg.messageType ?? (msg.isAI ? 'ai' : (isMe ? 'user' : 'admin'))
                    return (
                      <ChatBubble
                        key={msg._id}
                        msg={msg}
                        isMe={isMe}
                        messageType={messageType}
                        senderLabel={msg.senderName ?? (isMe ? 'You' : (isAdmin ? otherParticipantName(selectedConversation) : `Admin ${ADMIN_DISPLAY_NAME}`))}
                      />
                    )
                  })
                )}
                {isAiTyping && (
                  <div className='floating-connected-users-ai-typing'>
                    <div className='floating-connected-users-ai-typing-indicator'>
                      <span className='floating-connected-users-ai-typing-dot' />
                      <span className='floating-connected-users-ai-typing-dot' />
                      <span className='floating-connected-users-ai-typing-dot' />
                    </div>
                    <span className='floating-connected-users-ai-typing-text'>Maarefati Assistant is thinking…</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              {!isAdmin && selectedConversation && (
                <div className='floating-connected-users-ai-assistant-bar'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary floating-connected-users-ai-btn'
                    onClick={() => {
                      // Trigger AI by sending a message asking for help
                      const aiPrompt = '🤖 Ask Maarefati Assistant'
                      setChatInput(aiPrompt)
                    }}
                    disabled={sending}
                    title='Ask Maarefati AI Assistant for help'
                    aria-label='Ask AI Assistant'
                  >
                    🤖 Ask Maarefati Assistant
                  </button>
                </div>
              )}
              <div className='floating-connected-users-chat-emoji-bar'>
                {CHAT_QUICK_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type='button'
                    className='floating-connected-users-emoji-btn'
                    onClick={() => setChatInput((prev) => prev + emoji)}
                    disabled={sending || (!isAdmin && !selectedConversation)}
                    title='Insert emoji'
                    aria-label={`Insert ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <div className='floating-connected-users-chat-footer'>
                <input
                  type='file'
                  ref={fileInputRef}
                  accept='image/*'
                  className='d-none'
                  onChange={handleFileChange}
                  aria-hidden
                />
                <button
                  type='button'
                  className='btn btn-sm btn-light border floating-connected-users-attach-btn'
                  onClick={handleAttachImage}
                  disabled={sending || (!isAdmin && !selectedConversation)}
                  title='Send screenshot or image'
                  aria-label='Attach screenshot or image'
                >
                  <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-4' />
                </button>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder={!isAdmin && !selectedConversation ? 'Chat not available yet' : 'Type a message or paste screenshot (Ctrl+V)…'}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onPaste={handlePaste}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSend()
                    }
                  }}
                  disabled={sending || (!isAdmin && !selectedConversation)}
                />
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={handleSend}
                  disabled={sending || !chatInput.trim() || (!isAdmin && !selectedConversation)}
                >
                  Send
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function ChatBubble({
  msg,
  isMe,
  messageType,
  senderLabel,
}: {
  msg: ChatMessage
  isMe: boolean
  messageType: MessageType
  senderLabel: string
}) {
  const isImage = isImageContent(msg.content)
  const isAI = messageType === 'ai'
  const isSystem = messageType === 'system'
  
  return (
    <div
      className={`floating-connected-users-message-row ${
        isMe 
          ? 'floating-connected-users-message-row-me' 
          : isAI 
            ? 'floating-connected-users-message-row-ai'
            : isSystem
              ? 'floating-connected-users-message-row-system'
              : 'floating-connected-users-message-row-them'
      }`}
    >
      <div className='floating-connected-users-bubble-wrapper'>
        <span className='floating-connected-users-bubble-sender'>
          {isAI && <span className='floating-connected-users-ai-icon'>🤖</span>}
          {senderLabel}
          {isAI && msg.confidence !== undefined && (
            <span className='floating-connected-users-ai-confidence'>
              {' '}({Math.round(msg.confidence * 100)}%)
            </span>
          )}
        </span>
        <div
          className={`floating-connected-users-bubble ${
            isMe 
              ? 'floating-connected-users-bubble-me' 
              : isAI 
                ? 'floating-connected-users-bubble-ai'
                : isSystem
                  ? 'floating-connected-users-bubble-system'
                  : 'floating-connected-users-bubble-them'
          }`}
        >
          <div className='floating-connected-users-bubble-content'>
            {isImage ? (
              <img
                src={msg.content}
                alt='Screenshot'
                className='floating-connected-users-bubble-image'
              />
            ) : (
              msg.content
            )}
          </div>
          <span className='floating-connected-users-bubble-time'>
            {formatMessageTime(msg.createdAt)}
          </span>
        </div>
      </div>
    </div>
  )
}
